// BattleHeader.jsx
import React, { useRef, useCallback, useState, useEffect } from "react";
import { routes } from "../../routes";
import { useNavigate } from "react-router";
import { AiFillSafetyCertificate } from "react-icons/ai";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import "./battleHeader.css";
import FairnessModal from "../FairnessModal/FairnessModal";
import { ReactComponent as Swords } from "../../assets/icons/swords.svg";
import { ReactComponent as CrazyZap } from "../../assets/icons/crazy_zap.svg";

const BattleHeader = ({ battleDetail }) => {
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const [currentBoxIndex, setCurrentBoxIndex] = useState(0);
  const [fairnessModal, setFairnessModal] = useState(false);
  const isDarkMode = useSelector((state) => state?.theme?.darkMode);

  /* ── Derive display values ── */
  const animatingBox = battleDetail?.box_detail?.animating_box;
  const currentBox = battleDetail?.box_detail?.current_box;
  const displayBox =
    animatingBox && animatingBox > 0
      ? animatingBox
      : currentBox > 0
        ? currentBox
        : 1;

  const battlePrice =
    battleDetail?.stake_amount ||
    battleDetail?.box_detail?.battle_price ||
    "0.00";

  const displayBoxIndex = displayBox - 1;
  const currentBoxData =
    battleDetail?.boxes?.[displayBoxIndex]?.box ||
    battleDetail?.box_detail?.box;

  const boxes =
    battleDetail?.boxes?.map((boxItem) => ({
      id: boxItem.box.id,
      name: boxItem.box.name,
      slug: boxItem.box.slug,
      box_price: boxItem.box.box_price,
      box_image: boxItem.box.box_images?.image,
      rarity: boxItem.box.rarity,
      position: boxItem.position,
    })) || [];

  const boxPrice = currentBoxData?.box_price || battlePrice;

  /* ── Emoji fallback for missing images ── */
  const getBoxIcon = useCallback((rarity) => {
    switch (rarity?.toLowerCase()) {
      case "common":
        return "📦";
      case "rare":
        return "🎮";
      case "epic":
        return "⚡";
      case "legendary":
        return "👑";
      default:
        return "📦";
    }
  }, []);

  /* ── Shift active box to LEFT edge using transform ── */
  useEffect(() => {
    const container = scrollRef.current;
    if (!container || !boxes.length) return;

    const index = (displayBox || 1) - 1;
    const children = Array.from(container.children);
    if (!children[index]) return;

    /* Step 1 — Reset transform so offsetLeft values are layout-accurate */
    container.style.transition = "none";
    container.style.transform = "translateX(0px)";

    /* Step 2 — After browser re-paints, measure and apply the real shift */
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const target = container.children[index];
        if (!target) return;

        /* offsetLeft = distance from container's left origin to this box */
        const shift = target.offsetLeft;

        container.style.transition =
          "transform 0.45s cubic-bezier(0.4, 0, 0.2, 1)";
        container.style.transform = `translateX(-${shift}px)`;

        setCurrentBoxIndex(index);
      });
    });
  }, [displayBox, boxes.length]);

  return (
    <>
      <div className="battle-header-outer">
        <div
          className={`battle-header-wrap container ${isDarkMode ? "dark" : "light"}`}
        >
          <div className="bh-row-1">
            {/* Back Button */}
            <button
              className="bh-back"
              onClick={() => navigate(routes.BATTLES)}
              aria-label="Go back"
            >
              <ChevronLeft size={18} strokeWidth={2.5} />
            </button>

            {/* Cost + Mode Badges */}
            <div className="bh-cost-block">
              {battleDetail?.mode === "crazy" ? (
                <CrazyZap />
              ) : (
                <div className="bh-lightning">
                  <Swords
                    style={{ padding: "4px" }}
                    className="normal-mode-swords"
                  />
                </div>
              )}
              <span className="bh-cost-label text-AktivGrotesk-Bold">
                <span className="cost-text text-AktivGrotesk-Bold">
                  Cost:{"  "}
                </span>
                ${boxPrice}
              </span>
              <span className="bh-badges">
                {battleDetail?.private && (
                  <div className="mode-badge mode-badge--private">Private</div>
                )}
                {battleDetail?.mode === "crazy" && (
                  <div className="mode-badge mode-badge--crazy">Crazy Mode</div>
                )}
                {!battleDetail?.private && battleDetail?.mode !== "crazy" && (
                  <div className="mode-badge mode-badge--normal">Normal</div>
                )}
              </span>
            </div>
          </div>

          <div className="bh-row-2">
            {/* Box Strip */}
            <div className="bh-box-strip">
              <div className="bh-boxes-scroll" ref={scrollRef}>
                {boxes.map((box, i) => (
                  <div
                    key={box.id || i}
                    className={`bh-box-thumb ${i === currentBoxIndex ? "active" : ""}`}
                  >
                    <img
                      src={box.box_image}
                      alt={box.name}
                      draggable="false"
                      onError={(e) => {
                        const parent = e.currentTarget.parentElement;
                        e.currentTarget.style.display = "none";
                        parent.style.fontSize = "26px";
                        parent.style.display = "flex";
                        parent.style.alignItems = "center";
                        parent.style.justifyContent = "center";
                        parent.innerHTML = getBoxIcon(box?.rarity);
                      }}
                    />
                  </div>
                ))}
              </div>

              {boxes.length > 0 && (
                <span className="bh-counter text-AktivGrotesk-Regular">
                  {currentBoxIndex + 1}/{boxes.length}
                </span>
              )}
            </div>

            {/* Provably Fair */}
            {/* <div
              className="bh-fairness-btn"
              onClick={() => setFairnessModal(true)}
            >
              <AiFillSafetyCertificate className="bh-fair-icon" />
              <span>Fairness</span>
            </div> */}
            <Link to={routes?.PROVABLYFAIR} className="bh-fairness-btn">
              <AiFillSafetyCertificate className="bh-fair-icon" />
              <span>Fairness</span>
            </Link>
          </div>
        </div>
      </div>
      <FairnessModal
        show={fairnessModal}
        isDarkMode={isDarkMode}
        onClose={() => setFairnessModal(false)}
        battleDetail={battleDetail}
      />
    </>
  );
};

export default BattleHeader;
